import {
  Box,
  Button,
  IconButton,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {
  BarragemData,
  GeometryBarragem
} from "../../../context";
import { useEffect, useRef, useState } from "react";
import { googleSateliteHybrid, HidroLayer, HidroLayerGroup, HidroMap } from "../../mapa";
import { divIcon, Marker } from "leaflet";
import { Add, Architecture, ArrowDropDown, ArrowDropUp, Delete } from "@mui/icons-material";
import { BrechaConfig } from "./brecha";
import { BrechaSVG } from "../brechaSVG";
import { ConfirmModal, ErrorOnDialog } from "../../modals";
import { RequestError, useApi } from "../../../services";
import { GeomBarragem } from "../geomBarragem";
import { deepEqual } from "../../../core";

interface PropsListGeoms {
  barragem: BarragemData;
  onChange: (geometries: GeometryBarragem[]) => void;
}
export const ListGeometryConfig: React.FC<PropsListGeoms> = ({ onChange, barragem }) => {
  const api = useApi();

  const [_geometries, setGeometries] = useState(barragem.geometries);
  const [showEstruturas, setShowEstruturas] = useState(false);
  const [removeGeometryId, setRemoveGeometryId] = useState<number>();
  const [error, setError] = useState<RequestError>();

  // Atualizar dados
  useEffect(() => {
    const timer = setTimeout(() => onChange(_geometries), 500);
    return () => clearTimeout(timer);
  }, [_geometries]);

  const handleAddGeom = () => {
    api.barragem.addGeometry(
      barragem.id,
      barragem.lat * (1 + (((Math.random() * 2) - 1) / 100)),
      barragem.lon * (1 + (((Math.random() * 2) - 1) / 100)),
    ).then((value) => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setGeometries([..._geometries, value]);
    });
  };

  const handleUpdateGeom = (geometry: GeometryBarragem) => {
    api.barragem.updateGeometry(geometry).then((value) => {
      if ("error" in value) {
        setError(value);
        return;
      }

      const geomOriginal = _geometries.filter((geom) => geom.id == geometry.id)[0];
      if (deepEqual(value, geomOriginal)) return;

      const newGeoms = _geometries.map((geom) => {
        if (geom.id !== geometry.id) return geom;
        return value;
      });

      setGeometries([...newGeoms]);
    });
  };

  const handleRemoveGeom = () => {
    if (!removeGeometryId) return;

    api.barragem.removeGeometry(removeGeometryId).then((value) => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setGeometries(_geometries.filter((item) => item.id !== removeGeometryId));

    });
    setRemoveGeometryId(undefined);
  };

  return <>
    {/* Error modal */}
    <ErrorOnDialog
      open={Boolean(error)}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
      onClose={() => setError(undefined)}
    />

    {/* Modal para excluir geometrias */}
    <ConfirmModal
      open={Boolean(removeGeometryId)}
      title="Exclur Geometria"
      message="Deseja mesmo excluir esta geometria? Esta ação é irreversível!!"
      onConfirm={handleRemoveGeom}
      onCancel={() => setRemoveGeometryId(undefined)}
    />

    {/* Lista de dados */}
    <Box sx={{
      gap: "15px",
      width: "100%",
      padding: "15px",
      display: "flex",
      flexDirection: "column",
    }}>

      {/* Tooltip e Add Geom */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gapRow: "15px",
          cursor: "pointer",
        }}>

        {/* Tooltip */}
        <Tooltip title={showEstruturas ? "Esconder Estruturas" : "Mostrar Estruturas"}>
          <IconButton onClick={() => setShowEstruturas(!showEstruturas)}>
            {showEstruturas ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
        </Tooltip>

        <Typography
          fontWeight={"bold"}
          fontSize={18}
          onClick={() => setShowEstruturas(!showEstruturas)}
          sx={{
            cursor: "pointer",
          }}
        >
          Estruturas
        </Typography>

        <div
          onClick={() => setShowEstruturas(!showEstruturas)}
          style={{
            flex: 1,
            height: "26px",
            cursor: "pointer",
          }}
        />

        {/* Add Geom */}
        {showEstruturas && <Tooltip title="Adicionar nova Estrutura">
          <IconButton onClick={handleAddGeom}>
            <Add />
          </IconButton>
        </Tooltip>}
      </Box>

      {showEstruturas && _geometries.map((geom) => {
        return <GeometryConfig
          key={geom.id}
          latBarragem={barragem.lat}
          lonBarragem={barragem.lon}
          nomeBarragem={barragem.nome}
          geometry={geom}
          onRemove={(geom) => setRemoveGeometryId(geom.id)}
          onChange={handleUpdateGeom}
        />;
      })}

    </Box>
  </>;
};


interface Props {
  nomeBarragem: string;
  latBarragem: number;
  lonBarragem: number;
  onChange?: (geometry: GeometryBarragem) => void;
  onRemove?: (geometry: GeometryBarragem) => void;
  geometry: GeometryBarragem;
}

export const GeometryConfig: React.FC<Props> = ({ onChange, onRemove, geometry, latBarragem, lonBarragem, nomeBarragem }) => {
  const theme = useTheme();
  const heightGeom = 50;
  const iconSizeGeom = 20;
  const contrastText = theme.palette.getContrastText(theme.palette.background.default);

  const layerEstrutura = useRef<HidroLayerGroup>(new HidroLayerGroup({
    name: "Estrutura",
    showing: true,
    layers: [],
  }));

  const [geom, setGeometry] = useState<GeometryBarragem>(geometry);
  const [showBrecha, setShowBrecha] = useState(false);
  const [transitionRun, setTransitionRun] = useState(false);

  useEffect(() => {

    // Enviando os dados de volta
    const timer = setTimeout(() => {
      // Atualizando geometrias
      layerEstrutura.current.clearLayers();

      // Layer da Barragem
      try {
        const layerName = new Marker([latBarragem, lonBarragem]);
        const component: JSX.Element = (
          <ThemeProvider theme={theme}>
            <div style={{
              height: `${heightGeom}px`,
              width: "250px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}>
              <Typography
                color="background.paper"
                sx={{ textShadow: `2px 2px 6px ${contrastText}` }}>
                {nomeBarragem}
              </Typography>
            </div>
          </ThemeProvider>
        );
        const icon = divIcon({
          html: window.JSXToHTML(component),
          className: 'custom-svg-icon',
          iconAnchor: [iconSizeGeom / 2, heightGeom / 2],
        });

        layerName.setIcon(icon);

        layerEstrutura.current.push(new HidroLayer({
          name: nomeBarragem,
          showing: true,
          layer: layerName
        }));
      } catch {
        return;
      }

      // Layer da Estrutura
      layerEstrutura.current.push(
        new HidroLayer({
          showing: true,
          name: "Geometria",
          layer: new GeomBarragem([geom.lat, geom.lon], geom, theme.palette.geometries.geomBarragem)
        }));

      // Mapa
      layerEstrutura.current.push(new HidroLayer({
        name: "Google Satelite",
        showing: true,
        layer: googleSateliteHybrid(),
      }));

      layerEstrutura.current.centerBounds();

      if (onChange) onChange(geom);
    }, 500);

    return () => clearTimeout(timer); // Cancela o timer se o usuário continuar digitando
  }, [geom]);

  const onText = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    let newValue: number | string = value;

    const notNumber = ["brecha", 'estruturaVertente', 'finalidade', 'instrumentacao',
      'metodoAltiamento', 'tipo', 'tipoSecao'];
    if (!notNumber.includes(name)) {
      newValue = Number(newValue);
    }

    setGeometry(prev => ({
      ...prev,
      [name]: newValue,
    }));
  };

  return <Box sx={{
    width: "100%",
    height: "100%",
    rowGap: "15px",
    columnGap: "15px",
    border: "1px solid",
    padding: "15px",
    borderRadius: "15px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
  }}>
    {/* BTN Remove */}
    {onRemove &&
      <Box sx={{
        position: 'absolute',
        top: "15px",
        right: "15px",
        zIndex: 1000,
      }}>
        <IconButton onClick={() => onRemove(geom)}>
          <Delete />
        </IconButton>
      </Box>
    }

    {/* BTN Direita */}
    {showBrecha &&
      <Tooltip title="Mostrar Geometria">
        <IconButton onClick={() => {
          setShowBrecha(false);
          setTransitionRun(true);
        }}>
          <Architecture />
        </IconButton>
      </Tooltip>
    }

    {/* Mostrando a geometria */}
    <Box
      sx={{
        width: showBrecha ? 0 : "100%",
        padding: "15px",
        rowGap: "15px",
        columnGap: "15px",
        borderRadius: "15px",
        transition: 'all 0.8s ease-in-out',
      }}
      onTransitionEnd={() => setTransitionRun(false)}
    >
      {(!showBrecha || transitionRun) && <>
        {/* Tip, Finalidade e Seção*/}
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "15px",
        }}>
          {/* Tipo */}
          <TextField
            fullWidth
            label="Estrutura"
            name="tipo"
            value={geom.tipo}
            onChange={onText}
            variant="outlined"
          />

          {/* Finalidade */}
          <TextField
            fullWidth
            label="Finalidade"
            name="finalidade"
            value={geom.finalidade}
            onChange={onText}
            variant="outlined"
          />

          {/* Tipo Seção */}
          <TextField
            fullWidth
            label="Tipo da Seção"
            name="tipoSecao"
            value={geom.tipoSecao}
            onChange={onText}
            variant="outlined"
          />
        </div>

        {/* Espaçador */}
        <div style={{ height: "15px" }} />

        {/* Medidas */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "15px",
        }}>
          {/* Cota da crita */}
          <TextField
            fullWidth
            label="Cota da Crista (m)"
            name="cotaCrista"
            value={geom.cotaCrista.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* NA Op */}
          <TextField
            fullWidth
            label="NA Operacional (m)"
            name="naOperacao"
            value={geom.naOperacao.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* NA Norm */}
          <TextField
            fullWidth
            label="NA Normal (m)"
            name="naNormal"
            value={geom.naNormal.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* Alt. Máxima */}
          <TextField
            fullWidth
            label="Alt. Máxima (m)"
            name="altMax"
            value={geom.altMax.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* Comp. Crita */}
          <TextField
            fullWidth
            label="Comp. da Crista (m)"
            name="compCrista"
            value={geom.compCrista.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* Largura Crita */}
          <TextField
            fullWidth
            label="Lergura da Crista (m)"
            name="larguraCrista"
            value={geom.larguraCrista.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />
        </div>

        {/* Espaçador */}
        <div style={{ height: "15px" }} />

        {/* Medidas 2 */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "15px",
        }}>
          {/* Vol. Atual */}
          <TextField
            fullWidth
            label="Volume Atual (m³)"
            name="volAtual"
            value={geom.volAtual.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />

          {/* Vol. Max */}
          <TextField
            fullWidth
            label="Volume Máximo (m³)"
            name="volMax"
            value={geom.volMax.toString().replaceAll(".", ",")}
            onChange={(value) => {
              value.target.value = value.target.value.replaceAll(",", ".");
              onText(value);
            }}
            variant="outlined"
          />
        </div>

        {/* Espaçador */}
        <div style={{ height: "15px" }} />

        {/* Método de altiamento e Estru. vertente*/}
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "15px",
        }}>
          {/* Met. Alti, */}
          <TextField
            fullWidth
            label="Método de Altiamento"
            name="metodoAltiamento"
            value={geom.metodoAltiamento}
            onChange={onText}
            variant="outlined"
          />

          {/* Estrutura Vertente */}
          <TextField
            fullWidth
            label="Estrutura Vertente"
            name="estruturaVertente"
            value={geom.estruturaVertente}
            onChange={onText}
            variant="outlined"
          />
        </div>

        {/* Espaçador */}
        <div style={{ height: "15px" }} />

        {/* Lat Lon */}
        <Box sx={{
          padding: "15px",
          display: "flex",
          flexDirection: "row",
          height: "200px",
          width: "100%",
          gap: "15px",
        }}>
          {/* Localização */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
          }}>
            {/* Espaçador */}
            <div style={{ flex: 1 }} />

            {/* Localização da Estrutura */}
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}>
              {/* Lat e Lon */}
              {geom.lat && <TextField
                fullWidth
                label="Latitude"
                name="lat"
                value={geom.lat.toString().replaceAll(".", ",")}
                onChange={(value) => {
                  value.target.value = value.target.value.replaceAll(",", ".");
                  onText(value);
                }}
                variant="outlined"
              />}
              {geom.lon && <TextField
                fullWidth
                label="Longitude"
                name="lon"
                value={geom.lon.toString().replaceAll(".", ",")}
                onChange={(value) => {
                  value.target.value = value.target.value.replaceAll(",", ".");
                  onText(value);
                }}
                variant="outlined"
              />}
            </Box>

            {/* Espaçador */}
            <div style={{ flex: 1 }} />
          </div>

          {/* Mapa */}
          {!showBrecha && !transitionRun &&
            <div style={{ flex: 1 }}>
              <HidroMap
                animateStart={false}
                id="location-map"
                hidroLayers={layerEstrutura.current}
              />
            </div>}
        </Box>
      </>}
    </Box>


    {/* Mostrando a brecha */}
    <Box
      sx={{
        width: !showBrecha ? 0 : "100%",
        padding: "15px",
        rowGap: "15px",
        columnGap: "15px",
        borderRadius: "15px",
        transition: 'all 0.8s ease-in-out',
      }}
      onTransitionEnd={() => setTransitionRun(false)}
    >
      {(showBrecha || transitionRun) && <>
        <BrechaConfig
          geometria={geom}
          onChange={(brecha) => setGeometry(prev => ({ ...prev, brecha: brecha }))}
        />
      </>}
    </Box>


    {/* BTN Direita */}
    {!showBrecha &&
      <Tooltip title="Configurar Brecha de Ruptura">
        <Button onClick={() => {
          setShowBrecha(true);
          setTransitionRun(true);
        }}>
          <BrechaSVG
            style={{
              height: "250px",
              borderRadius: "25px",
            }}
            brecha={geometry.brecha}
            nomeEstrutura={geometry.tipo}
            showBarragem={false}
            showText={false}
            onClick={() => {
              setShowBrecha(true);
              setTransitionRun(true);
            }}
          />
        </Button>
      </Tooltip>
    }
  </Box >;
};